/* eslint-disable max-len */
import { AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { LocaleService } from '@next/core-lib/i18n';
import { RouterSelectors } from '@next/core-lib/router-store';
import { SiteActions } from '@next/core-lib/site';
import { TableColumnTypes, TableConfig } from '@next/core-lib/table';
import { UserSelectors } from '@next/core-lib/user';
import { WindowService, WindowServiceInjector } from '@next/core-lib/window';
import { select, Store } from '@ngrx/store';
import { combineLatest, of, ReplaySubject } from 'rxjs';
import { distinctUntilChanged, map, take, takeUntil } from 'rxjs/operators';
import { Admission } from 'src/app/crop-aid/crop-aid.model';
import { TreatmentType } from 'src/app/sales-order-period/models/sales-order-period.models';
import {
  AidAdviceDetail,
  CropFieldAdviceGroup,
  EnvironmentalImpactReport,
  ReportType,
  SiteState,
  TreatmentDetail,
} from 'src/app/shared/models';

import * as LegalRestrictionActions from '../../legal-restriction/store/legal-restriction.actions';
import { GetEnvironmentalImpactReport } from '../../store/actions';
import {
  getCropYear,
  getReportSort,
  selectCropAndCropAids,
  selectEnvironmentalImpactReport,
  selectLoggedInEmployeeAsArray,
  selectUniqueAdvicesWithoutTreatments,
  selectUniqueAdvicesWithTreatments,
} from '../../store/selectors';
import { ReportSort, ReportSortOption } from '../models';
import { ReportSortService } from '../services/report-sort.service';

@Component({
  selector: 'app-report',
  templateUrl: './report.component.html',
  styleUrls: ['./../reports.scss'],
  standalone: false,
})
export class ReportComponent implements AfterViewInit, OnInit, OnDestroy {
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly locale = inject(LocaleService);
  private readonly sortService = inject(ReportSortService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly window = inject<WindowService>(WindowServiceInjector);

  @ViewChild('titleColumn') titleColumn: TemplateRef<unknown>;
  @ViewChild('aquaticLifeColumn') aquaticLifeColumn: TemplateRef<unknown>;
  @ViewChild('soilLifeColumn') soilLifeColumn: TemplateRef<unknown>;
  @ViewChild('groundwaterWashoutColumn') groundwaterWashoutColumn: TemplateRef<unknown>;
  @ViewChild('hectaresColumn') hectaresColumn: TemplateRef<unknown>;

  destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);
  cropYear$ = this.store.pipe(select(getCropYear));
  uniqueAdvicesWithTreatments$ = this.store.pipe(
    select(
      selectUniqueAdvicesWithTreatments({
        treatmentType: this.getTreatmentType(),
        splitCropFields: this.route.snapshot.paramMap.get('splitCropFields'),
      }),
    ),
  );

  environmentalImpactPoints$ = this.store.pipe(select(selectEnvironmentalImpactReport));
  environmentalImpactPoints: EnvironmentalImpactReport;

  advicesWithTreatments: CropFieldAdviceGroup[] = [];
  sortedAdvicesWithTreatments: CropFieldAdviceGroup[] = [];
  uniqueAdvicesWithoutTreatments$ = this.store.pipe(
    select(
      selectUniqueAdvicesWithoutTreatments({
        treatmentType: this.getTreatmentType(),
        splitCropFields: this.route.snapshot.paramMap.get('splitCropFields'),
      }),
    ),
  );
  advicesWithoutTreatments: CropFieldAdviceGroup[] = [];
  cropAndCropAids$ = this.store.pipe(
    select(
      selectCropAndCropAids({
        treatmentType: this.getTreatmentType(),
        splitCropFields: this.route.snapshot.paramMap.get('splitCropFields'),
      }),
    ),
    distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
  );
  customer$ = this.store.pipe(select(UserSelectors.selectCustomer));
  relatedPeople$ = this.store.pipe(select(selectLoggedInEmployeeAsArray));

  showSplittedCropFields$ = this.store.pipe(
    select(RouterSelectors.getRouteParam('splitCropFields')),
  );
  showSplittedCropFields: boolean;

  reportType$ = this.store.pipe(select(RouterSelectors.getRouteParam('reportType')));
  isGBM$ = this.reportType$.pipe(
    map(
      (reportType) =>
        reportType === ReportType.GBMWithEnvironmentalImpactPoints,
    ),
  );
  showEnvironmentalImpactPoints = false;
  showEnvironmentalImpactPoints$ = this.reportType$.pipe(
    map((reportType) => reportType === ReportType.GBMWithEnvironmentalImpactPoints),
    map((showEnvironmentalImpactPoints) => this.showEnvironmentalImpactPoints = showEnvironmentalImpactPoints,
    ));

  pageTitle$ = combineLatest([
    this.cropYear$,
    this.customer$,
    this.isGBM$,
    this.showSplittedCropFields$,
  ]).pipe(
    map(
      ([cropYear, customer, isGBM, showSplittedCropFields]) =>
        `${customer?.name ? customer.name : ''} ${this.locale.translate(
          isGBM
            ? 'createGBMAdviceReportWithEIP'
            : showSplittedCropFields
              ? 'createFertilizationAdviceReportSplitFields'
              : 'createFertilizationAdviceReport',
        )} ${cropYear}`,
    ),
  );
  showReportsCheckCookie = 'AGR_SHOW_REPORTS_CHECKS';
  showCheckboxes$ = of(this.window.getCookie(this.showReportsCheckCookie) === 'true');

  aidAdviceColumns$ = combineLatest([this.isGBM$, this.showCheckboxes$]).pipe(
    map(([isGBM, showCheckboxes]) => [
      'dosagePerHectare',
      'product',
      'total',
      ...(isGBM
        ? [
          'admissionNumbers',
          'maxDosage',
          'yearlyDosage',
          'safetyPeriodInDays',
          'intervalInDays',
          'numberOfTimesPerYear',
        ]
        : []),
      ...(showCheckboxes ? ['checkboxes'] : []),
    ]),
  );

  tableConfigTotalsWithAdviceConfig: TableConfig = {
    dataSource: new MatTableDataSource(),
    selectColumn: false,
    pagination: false,
    header: true,
    footer: false,
    disableCards: true,
    columns: [
      {
        id: 'title',
        label: this.locale.translate('title'),
        type: TableColumnTypes.TEMPLATE,
        className: 'title-column',
      },
      {
        id: 'aquaticLife',
        type: TableColumnTypes.TEMPLATE,
        className: 'aquatic-life-column',
      },
      {
        id: 'soilLife',
        type: TableColumnTypes.TEMPLATE,
        className: 'soil-life-column',
        headerClassName: 'soil-life-header',
      },
      {
        id: 'groundwaterWashout',
        type: TableColumnTypes.TEMPLATE,
        className: 'groundwater-washout-column',
        headerClassName: 'groundwater-washout-header',
      },
      {
        id: 'hectares',
        label: this.locale.translate('hectares'),
        type: TableColumnTypes.TEMPLATE,
        className: 'hectares-column',
        headerClassName: 'hectares-column',
      },
    ],
  };

  tableConfigTotalsWithAdvice: TableConfig = this.tableConfigTotalsWithAdviceConfig;

  tableConfigTotalsWithoutAdvice: TableConfig = {
    dataSource: new MatTableDataSource(),
    selectColumn: false,
    pagination: false,
    header: true,
    footer: false,
    disableCards: true,
    columns: [
      {
        id: 'title',
        label: this.locale.translate('title'),
        type: TableColumnTypes.TEMPLATE,
      },
      {
        id: 'hectares',
        label: this.locale.translate('hectares'),
        type: TableColumnTypes.TEMPLATE,
        className: 'hectares',
        headerClassName: 'hectares',
      },
    ],
  };

  reportSort$ = this.store.pipe(select(getReportSort));
  reportSort: ReportSort = { option: ReportSortOption.sortCropFieldName, descending: false };

  buttonItems$ = this.sortService
    .getButtonItems()
    .pipe(map((buttonItems) => buttonItems.filter(() => this.showSplittedCropFields)));

  cropFieldNameFunc = (advice: CropFieldAdviceGroup) =>
    advice.cropFieldAdvices.length > 0 ? advice.cropFieldAdvices[0].name : '';
  cropNameFunc = (advice: CropFieldAdviceGroup) => `${advice.cropName} ${advice.cropVarietyName}`;

  ngAfterViewInit() {
    this.isGBM$.subscribe((isGBM) => {
      setTimeout(() => {
        const newConfigWithAdvice = { ...this.tableConfigTotalsWithAdviceConfig };
        newConfigWithAdvice.columns[0].template = this.titleColumn;
        newConfigWithAdvice.columns[4].template = this.hectaresColumn;

        if (isGBM) {
          newConfigWithAdvice.columns[1].template = this.aquaticLifeColumn;
          newConfigWithAdvice.columns[2].template = this.soilLifeColumn;
          newConfigWithAdvice.columns[3].template = this.groundwaterWashoutColumn;
        } else {
          newConfigWithAdvice.columns = newConfigWithAdvice.columns.filter((column) =>
            !['aquaticLife', 'soilLife', 'groundwaterWashout'].includes(column.id),
          );
        }

        this.tableConfigTotalsWithAdvice = newConfigWithAdvice;

        const newConfigWithoutAdvice = { ...this.tableConfigTotalsWithoutAdvice };
        newConfigWithoutAdvice.columns[0].template = this.titleColumn;
        newConfigWithoutAdvice.columns[1].template = this.hectaresColumn;
        this.tableConfigTotalsWithoutAdvice = newConfigWithoutAdvice;
      }, 0);
    });
  }

  ngOnInit() {
      this.store.dispatch(
        new GetEnvironmentalImpactReport(
          JSON.parse(this.route.snapshot.paramMap.get('cropFieldAdviceIdsReport')),
        ),
      );

    this.cropAndCropAids$.pipe(takeUntil(this.destroyed$)).subscribe((cropAndCropAids) => {
      if (cropAndCropAids.length) {
        this.store.dispatch(
          LegalRestrictionActions.getLegalRestrictions({
            cropAndCropAids,
          }),
        );
      }
    });

    combineLatest([this.uniqueAdvicesWithTreatments$, this.environmentalImpactPoints$, this.showEnvironmentalImpactPoints$])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([advices, environmentalImpactPoints, showEnvironmentalImpactPoints]) => {
        this.advicesWithTreatments = advices;
        this.environmentalImpactPoints = environmentalImpactPoints;
        this.showEnvironmentalImpactPoints = showEnvironmentalImpactPoints;
        this.sortedAdvicesWithTreatments = this.sortAdvices(
          this.advicesWithTreatments,
          this.showSplittedCropFields,
          this.reportSort,
          this.cropFieldNameFunc,
          this.cropNameFunc,
        );
        this.tableConfigTotalsWithAdvice.dataSource.data = this.getTableTotalsDataSource(
          this.sortedAdvicesWithTreatments,
          'withAdvice',
          this.showEnvironmentalImpactPoints,
        );
      });

    this.uniqueAdvicesWithoutTreatments$.pipe(takeUntil(this.destroyed$)).subscribe((advices) => {
      this.advicesWithoutTreatments = advices;
      this.tableConfigTotalsWithoutAdvice.dataSource.data = this.getTableTotalsDataSource(
        this.sortAdvices(
          this.advicesWithoutTreatments,
          this.showSplittedCropFields,
          this.reportSort,
          this.cropFieldNameFunc,
          this.cropNameFunc,
        ),
        'withoutAdvice',
      );
    });

    this.pageTitle$.pipe(takeUntil(this.destroyed$)).subscribe((pageTitle) => {
      this.store.dispatch(SiteActions.updatePageTitle({ pageTitle }));
    });

    this.showSplittedCropFields$.subscribe(
      (showSplittedCropFields) => (this.showSplittedCropFields = !!showSplittedCropFields),
    );

    this.reportSort$.subscribe((reportSort) => {
      this.reportSort = reportSort;
      this.sortedAdvicesWithTreatments = this.sortAdvices(
        this.advicesWithTreatments,
        this.showSplittedCropFields,
        this.reportSort,
        this.cropFieldNameFunc,
        this.cropNameFunc,
      );
      this.tableConfigTotalsWithAdvice.dataSource.data = this.getTableTotalsDataSource(
        this.sortedAdvicesWithTreatments,
        'withAdvice',
        this.showEnvironmentalImpactPoints,
      );
      this.tableConfigTotalsWithoutAdvice.dataSource.data = this.getTableTotalsDataSource(
        this.sortAdvices(
          this.advicesWithoutTreatments,
          this.showSplittedCropFields,
          this.reportSort,
          this.cropFieldNameFunc,
          this.cropNameFunc,
        ),
        'withoutAdvice',
      );
    });
  }

  ngOnDestroy() {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  print() {
    if (this.window) {
      this.window.print();
    }
  }

  showCheckboxes() {
    this.showCheckboxes$.pipe(take(1)).subscribe((showCheckboxes) => {
      this.window.setPlatformLocalCookie(this.showReportsCheckCookie, String(!showCheckboxes));
      this.window.reload();
    });
  }

  getTableTotalsDataSource(
    advices: CropFieldAdviceGroup[],
    tableType: 'withAdvice' | 'withoutAdvice',
    showEnvironmentalImpactPoints = false,
  ) {
    const dataSource = [];
    let totalHectares = 0;
    let totalAquaticLife = 0;
    let totalSoilLife = 0;
    let totalGroundwaterWashout = 0;

    if (showEnvironmentalImpactPoints && this.tableConfigTotalsWithAdvice?.columns?.[2]) {
      this.tableConfigTotalsWithAdvice.columns[2].label = this.locale.translate('environmentalImpactPoints');
    }

    const addEnvironmentalImpactHeader = () => {
      dataSource.push({
        title: '<div class="secondary-header"></div>',
        aquaticLife: `<div class="environmental-impact-header secondary-header">${this.locale.translate('aquaticLife')}</div>`,
        soilLife: `<div class="environmental-impact-header secondary-header">${this.locale.translate('soilLife')}</div>`,
        groundwaterWashout: `<div class="environmental-impact-header secondary-header">${this.locale.translate('groundwaterWashout')}</div>`,
        hectares: '<div class="secondary-header"></div>',
      });
    };

    const createAdviceRow = (advice) => {
      const titleDetails =
        advice.cropFieldAdvices.length === 1
          ? advice.cropFieldAdvices[0].name
          : `${advice.cropFieldAdvices.length} ${this.locale.translate('cropFields')}:`;
      const title = `${advice.cropName}, ${titleDetails}`;
      dataSource.push({
        title,
        hectares: `<div class="spacing--padding-right">${advice.totalHectares?.toFixed(2)}</div>`,
      });
    };

    const createAdviceRowWithEIP = (advice, eip) => {
      const titleDetails =
        advice.cropFieldAdvices.length === 1
          ? advice.cropFieldAdvices[0].name
          : `${advice.cropFieldAdvices.length} ${this.locale.translate('cropFields')}:`;
      const title = `${advice.cropName}, ${titleDetails}`;
      dataSource.push({
        title,
        aquaticLife: eip ? eip.totals.aquaticLife : 0,
        soilLife: eip ? eip.totals.soilLife : 0,
        groundwaterWashout: eip ? eip.totals.groundwaterWashout : 0,
        hectares: `<div class="spacing--padding-right">${advice.totalHectares?.toFixed(2)}</div>`,
      });
    };

    // Helper to create rows for individual crop field advices if multiple exist
    const addCropFieldRows = (cropFieldAdvices) => {
      cropFieldAdvices.forEach((cropFieldAdvice) => {
        dataSource.push({
          title: `<div class="spacing--padding-left text-color--gray3">${cropFieldAdvice.name}</div>`,
          aquaticLife: '<div></div>',
          soilLife: '<div></div>',
          groundwaterWashout: '<div></div>',
          hectares: `<div class="text--right text-color--gray3">${cropFieldAdvice.areaInHectares?.toFixed(2)}</div>`,
        });
        totalHectares += cropFieldAdvice.areaInHectares;
      });
    };

    // Helper to add the total row
    const addTotalRow = () => {
      dataSource.push({
        title: `<div class="total">${this.locale.translate('total')}</div>`,
        aquaticLife: showEnvironmentalImpactPoints ? `<div class="spacing--padding-right total">${this.roundNumber(totalAquaticLife, 1)}</div>` : '',
        soilLife: showEnvironmentalImpactPoints ? `<div class="spacing--padding-right total">${this.roundNumber(totalSoilLife, 1)}</div>` : '',
        groundwaterWashout:  showEnvironmentalImpactPoints ? `<div class="spacing--padding-right total">${this.roundNumber(totalGroundwaterWashout, 1)}</div>` : '',
        hectares: `<div class="spacing--padding-right total">${totalHectares.toFixed(2)}</div>`,
      });
    };

    // Adding header if tableType is 'withAdvice'
    if (tableType === 'withAdvice' && showEnvironmentalImpactPoints) {
      addEnvironmentalImpactHeader();
    }

    advices.forEach((advice) => {
      const eip = this.environmentalImpactPoints?.cropFields.find(
        (x) => x.cropFieldId === advice.cropFieldAdvices[0].cropFieldId,
      );

      if (showEnvironmentalImpactPoints) {
        createAdviceRowWithEIP(advice, eip);
        totalAquaticLife += eip?.totals.aquaticLife || 0;
        totalSoilLife += eip?.totals.soilLife || 0;
        totalGroundwaterWashout += eip?.totals.groundwaterWashout || 0;
      } else {
        createAdviceRow(advice);
      }

      if (advice.cropFieldAdvices.length > 1) {
        addCropFieldRows(advice.cropFieldAdvices);
      } else {
        totalHectares += advice.totalHectares;
      }
    });

    if (dataSource.length) {
      addTotalRow();
    }

    return dataSource;
  }

  getTreatmentAmount(
    advice: CropFieldAdviceGroup,
    treatment: TreatmentDetail,
    aidAdvice: AidAdviceDetail,
  ) {
    return (
      aidAdvice.dosage *
      (treatment.shouldUseAreaInHectaresManual
        ? treatment.areaInHectaresManual
        : advice.totalHectares)
    );
  }

  getAdmissionNumberString(admissions: Admission[]) {
    if (!admissions || admissions.length < 1) {
      return;
    }

    return `${admissions.map((admission) => admission.number).join(', ')}`;
  }

  back() {
    this.router.navigate(['/']);
  }

  buttonAction(data: string) {
    this.sortService.handleButtonAction(data);
  }

  protected getBgClass = (value: number): string => {
    if (value && value < 100) {
      return 'success';
    } else if (value >= 100 && value < 1000) {
      return 'alert';
    } else if (value >= 1000) {
      return 'warning';
    }
    return '';
  };

  protected roundNumber = (value, decimals: number) => {
    if (typeof value === 'number') {
      const factor = Math.pow(10, decimals);
      return Math.round(value * factor) / factor;
    }
    return value;
  };

  private sortAdvices<T>(
    advices: T[],
    showSplittedCropFields: boolean,
    reportSort: ReportSort,
    cropFieldNameFunc: (x: T) => string,
    cropNameFunc: (x: T) => string,
  ): T[] {
    return this.sortService.sort({
      elements: advices,
      option: reportSort.option,
      descending: reportSort.descending,
      cropFieldNameFunc: showSplittedCropFields ? cropFieldNameFunc : cropNameFunc,
      cropNameFunc,
    });
  }

  private getTreatmentType(): TreatmentType {
    const treatmentType = this.route.snapshot.paramMap.get('reportType');

    switch (treatmentType) {
    case ReportType.GBMWithEnvironmentalImpactPoints:
      return TreatmentType.GBM;
    case ReportType.Fertilization:
      return TreatmentType.Fertilization;
    default:
      return treatmentType as TreatmentType;
    }
  }
}
